/**
 * AroundMeService — country-aware nearby POI suggestions.
 *
 * Two responsibilities:
 *
 * 1. **Etiquette table**: for each known country, return the top 4-5 POI
 *    types people search for daily (Google Maps trends + Foursquare /
 *    HalalTrip / DataReportal heuristics). Critical rule: "Halal" is
 *    NEVER suggested in Muslim-majority countries (would imply the rest
 *    aren't halal — culturally incorrect). It's surfaced as the top
 *    etiquette in non-Muslim diaspora countries (FR, GB, DE, US, CA).
 *
 * 2. **Nearby query**: when the user explicitly taps an etiquette pill,
 *    issues an OSM Overpass API query for matching POIs within a 1.5 km
 *    radius. Free, no API key. Cached 1 h client-side via swr().
 *
 * Privacy: lat/lon stays on-device for the Overpass query. No PII forwarded.
 *
 * Adopted 2026-04-30 per the daily-search audit. Rule: "Open AWENI = get
 * 90% of daily searches resolved without searching."
 */

import { swr } from './swr';

export interface PoiEtiquette {
  /** Stable identifier — used as a cache key + Overpass query selector. */
  id: string;
  /** Single emoji glyph for the pill. */
  icon: string;
  /** Localized labels per i18n key. */
  labels: {
    en: string;
    fr: string;
    ar: string;
    es: string;
    de: string;
  };
  /** Overpass API query fragment (e.g., `amenity=pharmacy`). */
  query: string;
  /** Display priority weight (higher = more prominent). */
  weight: number;
}

/**
 * ISO 3166-1 alpha-2 codes of countries where "Halal" is the default and
 * should NEVER be shown as a POI etiquette. Showing it would be culturally
 * insulting (implies the rest of the food isn't halal). Source: OIC member
 * states + countries with > 50% Muslim population.
 */
const MUSLIM_MAJORITY = new Set([
  'SA', 'AE', 'QA', 'KW', 'BH', 'OM', 'YE',
  'JO', 'LB', 'SY', 'IQ', 'EG', 'LY', 'TN', 'DZ', 'MA', 'MR', 'PS',
  'SD', 'SO', 'DJ', 'KM',
  'TR', 'IR', 'AF',
  'PK', 'BD', 'MV', 'BN',
  'ID', 'MY',
  'SN', 'ML', 'NE', 'GM', 'GN', 'BF', 'TD',
  'AZ', 'TM', 'TJ', 'UZ', 'KG', 'KZ',
]);

/**
 * Master etiquette catalog. The home strip picks 4-5 of these per country
 * via the country-→-id ranking below.
 */
const ETIQUETTES: Record<string, PoiEtiquette> = {
  pharmacy: {
    id: 'pharmacy', icon: '💊',
    labels: { en: 'Pharmacy', fr: 'Pharmacie', ar: 'صيدلية', es: 'Farmacia', de: 'Apotheke' },
    query: 'amenity=pharmacy', weight: 9,
  },
  atm: {
    id: 'atm', icon: '💳',
    labels: { en: 'ATM', fr: 'Distributeur', ar: 'صراف آلي', es: 'Cajero', de: 'Geldautomat' },
    query: 'amenity=atm', weight: 8,
  },
  cafe: {
    id: 'cafe', icon: '☕',
    labels: { en: 'Café', fr: 'Café', ar: 'مقهى', es: 'Café', de: 'Café' },
    query: 'amenity=cafe', weight: 7,
  },
  restaurant: {
    id: 'restaurant', icon: '🍽️',
    labels: { en: 'Restaurant', fr: 'Restaurant', ar: 'مطعم', es: 'Restaurante', de: 'Restaurant' },
    query: 'amenity=restaurant', weight: 7,
  },
  bakery: {
    id: 'bakery', icon: '🥖',
    labels: { en: 'Bakery', fr: 'Boulangerie', ar: 'مخبز', es: 'Panadería', de: 'Bäckerei' },
    query: 'shop=bakery', weight: 7,
  },
  fuel: {
    id: 'fuel', icon: '⛽',
    labels: { en: 'Gas station', fr: 'Station-service', ar: 'محطة وقود', es: 'Gasolinera', de: 'Tankstelle' },
    query: 'amenity=fuel', weight: 6,
  },
  supermarket: {
    id: 'supermarket', icon: '🛒',
    labels: { en: 'Supermarket', fr: 'Supermarché', ar: 'سوبر ماركت', es: 'Supermercado', de: 'Supermarkt' },
    query: 'shop=supermarket', weight: 6,
  },
  mall: {
    id: 'mall', icon: '🏬',
    labels: { en: 'Mall', fr: 'Centre commercial', ar: 'مول', es: 'Centro comercial', de: 'Einkaufszentrum' },
    query: 'shop=mall', weight: 7,
  },
  parking: {
    id: 'parking', icon: '🅿️',
    labels: { en: 'Parking', fr: 'Parking', ar: 'موقف سيارات', es: 'Aparcamiento', de: 'Parkplatz' },
    query: 'amenity=parking', weight: 6,
  },
  hospital: {
    id: 'hospital', icon: '🏥',
    labels: { en: 'Hospital', fr: 'Hôpital', ar: 'مستشفى', es: 'Hospital', de: 'Krankenhaus' },
    query: 'amenity=hospital', weight: 6,
  },
  // Diaspora-only etiquettes — filtered out in MUSLIM_MAJORITY countries.
  mosque: {
    id: 'mosque', icon: '🕌',
    labels: { en: 'Mosque', fr: 'Mosquée', ar: 'مسجد', es: 'Mezquita', de: 'Moschee' },
    query: 'amenity=place_of_worship][religion=muslim', weight: 10,
  },
  halal_restaurant: {
    id: 'halal_restaurant', icon: '🍖',
    labels: { en: 'Halal Restaurant', fr: 'Restaurant Halal', ar: 'مطعم حلال', es: 'Restaurante Halal', de: 'Halal-Restaurant' },
    query: 'cuisine=halal', weight: 9,
  },
  halal_butcher: {
    id: 'halal_butcher', icon: '🥩',
    labels: { en: 'Halal Butcher', fr: 'Boucherie Halal', ar: 'جزار حلال', es: 'Carnicería Halal', de: 'Halal-Metzgerei' },
    query: 'shop=butcher][diet:halal=yes', weight: 8,
  },
};

/**
 * Country code → ranked list of etiquette IDs. The render filter further
 * removes Muslim-majority halal/mosque entries.
 *
 * `_default` is used when country is unknown.
 */
const COUNTRY_RANKINGS: Record<string, string[]> = {
  // Gulf
  SA: ['pharmacy', 'atm', 'restaurant', 'cafe', 'supermarket', 'fuel'],
  AE: ['cafe', 'restaurant', 'pharmacy', 'mall', 'atm', 'parking'],
  QA: ['cafe', 'restaurant', 'mall', 'pharmacy', 'atm', 'fuel'],
  KW: ['restaurant', 'cafe', 'pharmacy', 'mall', 'atm', 'supermarket'],
  BH: ['cafe', 'restaurant', 'pharmacy', 'atm', 'mall', 'supermarket'],
  OM: ['pharmacy', 'restaurant', 'atm', 'cafe', 'fuel', 'supermarket'],
  // Levant + Egypt
  EG: ['pharmacy', 'atm', 'cafe', 'restaurant', 'supermarket', 'bakery'],
  JO: ['pharmacy', 'restaurant', 'atm', 'cafe', 'supermarket', 'fuel'],
  LB: ['pharmacy', 'cafe', 'atm', 'restaurant', 'supermarket', 'bakery'],
  // Maghreb
  MA: ['pharmacy', 'cafe', 'atm', 'bakery', 'restaurant', 'supermarket'],
  DZ: ['pharmacy', 'cafe', 'atm', 'bakery', 'restaurant', 'fuel'],
  TN: ['cafe', 'pharmacy', 'atm', 'restaurant', 'bakery', 'supermarket'],
  // Turkey
  TR: ['pharmacy', 'cafe', 'restaurant', 'atm', 'bakery', 'supermarket'],
  // South Asia Muslim
  PK: ['pharmacy', 'atm', 'restaurant', 'bakery', 'fuel', 'hospital'],
  BD: ['pharmacy', 'atm', 'restaurant', 'hospital', 'fuel', 'supermarket'],
  // SE Asia Muslim
  ID: ['restaurant', 'atm', 'pharmacy', 'cafe', 'mall', 'fuel'],
  MY: ['cafe', 'restaurant', 'pharmacy', 'atm', 'mall', 'fuel'],
  // Sub-Saharan Muslim
  SN: ['pharmacy', 'restaurant', 'atm', 'fuel', 'supermarket', 'cafe'],
  ML: ['pharmacy', 'restaurant', 'atm', 'fuel', 'supermarket', 'cafe'],
  // Diaspora — Europe (mosque + halal first)
  FR: ['mosque', 'halal_restaurant', 'halal_butcher', 'pharmacy', 'atm', 'cafe'],
  GB: ['mosque', 'halal_restaurant', 'halal_butcher', 'pharmacy', 'atm', 'cafe'],
  DE: ['mosque', 'halal_restaurant', 'halal_butcher', 'pharmacy', 'atm', 'cafe'],
  ES: ['mosque', 'halal_restaurant', 'pharmacy', 'halal_butcher', 'atm', 'cafe'],
  IT: ['mosque', 'halal_restaurant', 'pharmacy', 'atm', 'cafe', 'halal_butcher'],
  NL: ['mosque', 'halal_restaurant', 'halal_butcher', 'pharmacy', 'atm', 'cafe'],
  BE: ['mosque', 'halal_restaurant', 'halal_butcher', 'pharmacy', 'atm', 'cafe'],
  // Diaspora — North America
  US: ['mosque', 'halal_restaurant', 'pharmacy', 'atm', 'cafe', 'fuel'],
  CA: ['mosque', 'halal_restaurant', 'pharmacy', 'atm', 'cafe', 'fuel'],
  // India (Muslim minority)
  IN: ['mosque', 'halal_restaurant', 'pharmacy', 'atm', 'restaurant', 'hospital'],
  // Default fallback
  _default: ['pharmacy', 'restaurant', 'atm', 'cafe', 'supermarket', 'fuel'],
};

export interface PoiResult {
  name: string;
  /** Distance in meters, computed client-side from user coords. */
  distanceM: number;
  /** OpenStreetMap node URL for "open in OSM" link, or `null`. */
  osmUrl: string | null;
}

export class AroundMeService {
  /**
   * Return the top 4-5 etiquettes for the given country, with halal/mosque
   * filtered out where it would be culturally inappropriate.
   */
  getEtiquettesForCountry(countryCode: string | null, limit = 4): PoiEtiquette[] {
    const cc = (countryCode || '').toUpperCase();
    const ranking = COUNTRY_RANKINGS[cc] || COUNTRY_RANKINGS._default;
    const isMuslimMajority = MUSLIM_MAJORITY.has(cc);
    const filtered = ranking.filter((id) => {
      if (!isMuslimMajority) return true;
      // In Muslim-majority countries, drop "halal_*" — implicit default.
      // Mosque also dropped: every neighborhood has one; users don't search.
      if (id === 'mosque' || id === 'halal_restaurant' || id === 'halal_butcher') return false;
      return true;
    });
    return filtered.slice(0, limit).map((id) => ETIQUETTES[id]).filter(Boolean);
  }

  /**
   * Query OpenStreetMap Overpass API for nearby POIs matching the etiquette.
   * Returns top 5 sorted by distance. Cached 1 h via swr() so repeat taps
   * within the same hour are instant.
   *
   * Free, no API key. Respect Overpass fair-use (a few rps max).
   */
  async findNearby(
    etiquette: PoiEtiquette,
    lat: number,
    lng: number,
    radiusM = 1500,
  ): Promise<PoiResult[]> {
    const key = `aroundme:${etiquette.id}:${lat.toFixed(2)}:${lng.toFixed(2)}`;
    const result = await swr<PoiResult[]>(
      key,
      () => this._fetchOverpass(etiquette, lat, lng, radiusM),
      { freshTtlMs: 60 * 60 * 1000, staleTtlMs: 24 * 60 * 60 * 1000 },
    );
    return result || [];
  }

  private async _fetchOverpass(
    etiquette: PoiEtiquette,
    lat: number,
    lng: number,
    radiusM: number,
  ): Promise<PoiResult[]> {
    // Overpass QL: search nodes + ways + relations matching the tag within radius.
    // We use the lighter `[out:json][timeout:6]` and a single-tag selector.
    const query = `[out:json][timeout:6];(node[${etiquette.query}](around:${radiusM},${lat},${lng});way[${etiquette.query}](around:${radiusM},${lat},${lng}););out center 30;`;
    const url = `https://overpass-api.de/api/interpreter?data=${encodeURIComponent(query)}`;
    const resp = await fetch(url, { cache: 'force-cache' });
    if (!resp.ok) return [];
    const data = await resp.json() as {
      elements: Array<{
        type: 'node' | 'way' | 'relation';
        id: number;
        lat?: number;
        lon?: number;
        center?: { lat: number; lon: number };
        tags?: Record<string, string>;
      }>;
    };
    const items: PoiResult[] = [];
    for (const el of (data.elements || [])) {
      const itemLat = el.lat ?? el.center?.lat;
      const itemLng = el.lon ?? el.center?.lon;
      if (typeof itemLat !== 'number' || typeof itemLng !== 'number') continue;
      const name = el.tags?.name || el.tags?.['name:en'] || el.tags?.['brand'] || '';
      items.push({
        name: name.trim() || etiquette.labels.en,
        distanceM: Math.round(this._haversineMeters(lat, lng, itemLat, itemLng)),
        osmUrl: `https://www.openstreetmap.org/${el.type}/${el.id}`,
      });
    }
    items.sort((a, b) => a.distanceM - b.distanceM);
    return items.slice(0, 5);
  }

  private _haversineMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371000;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }
}

export const aroundMeService = new AroundMeService();
